'use client';

import React, { useEffect, useState } from 'react';
import styles from '$modules/onboarding/module-components/onboarding/commom.module.scss';
import { Button, Icon, validIcons, ButtonSize, ButtonType, Toast, Loader, addToast } from '@brahma/components';
import { Input } from '@brahma/components';
import useLocale from '$hooks/use-locale';
import { ONBOARDING } from '$locale/constants';
import { SubHeader } from '@brahma/components';
import { useInputsValidation } from '@brahma/hooks/use-inputs-validation';
import { isEmptyString, validateEmail } from '@brahma/utils';
import { AnalyticsManager, useMutation, useAuthBroadcast, AuthEvent } from '@brahma/hooks';
import { API_URLS } from '$constants/apis';
import type { LoginBodyParameter, IBFFResponse, ClientSignInResponse } from '@dfg/brahma-types';
import { POST } from '@brahma/utils';
import useGlobalContext from '$hooks/use-global-context';
import { ContextAction } from '$context/global-context/types';
import { useLoadingRouter } from '@brahma/hooks';
import { PAGE_URLS } from '$constants/index';
import { TOAST_ERROR_TIME } from '$constants/common';
import { CLIENT_ROLE, COMMON_ROLE, DTD_ROLE, DMW_CLIENT_ROLE_USER } from '$modules/onboarding/constants/layout';
const Login = () => {
  const {
    t
  } = useLocale(ONBOARDING);
  const [showBadge, setShowBadge] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const globalContext = useGlobalContext();
  const {
    routerPush,
    loading: routerLoading
  } = useLoadingRouter();
  const {
    values,
    errors,
    handleChange,
    handleSubmit
  } = useInputsValidation({
    email: '',
    password: ''
  }, {
    password: [(value: string) => {
      return isEmptyString(value) ? t('login.error2') : '';
    }],
    email: [validateEmail(t('login.error1'))]
  });
  const {
    data: loginData,
    mutateAsync,
    loading,
    error: loginError
  } = useMutation<IBFFResponse<ClientSignInResponse>, LoginBodyParameter>({
    url: API_URLS.logIn,
    options: {
      method: POST
    }
  });
  const callMutateAsync = () => {
    mutateAsync({
      email: values.email as string,
      password: values.password as string
    });
  };
  const onSubmit = () => {
    setShowBadge(false);
    setSubmitClicked(true);
    handleSubmit(callMutateAsync);
  };
  const handleForgotPassword = () => {
    routerPush(PAGE_URLS.forgotPassword);
  };
  const {
    broadcastAuthEvent
  } = useAuthBroadcast();
  useEffect(() => {
    if (loginData) {
      setShowBadge(true);
      if (loginData.code === 200) {
        globalContext.dispatch({
          type: ContextAction.setUser,
          payload: {
            ...loginData?.data
          }
        });

        // updating the user data  click stream after user has been successfully logged in.
        AnalyticsManager.getInstance().updateUserData({
          ...loginData?.data
        });
        broadcastAuthEvent(AuthEvent.LOGIN);
        const roles = loginData?.data?.roles ?? [];
        if (roles.length && roles.includes(DTD_ROLE) && roles.includes(COMMON_ROLE) && !roles.includes(CLIENT_ROLE)) {
          routerPush(PAGE_URLS.uploadDocument);
        } else if (roles.includes(DMW_CLIENT_ROLE_USER)) {
          routerPush(PAGE_URLS.analyticsDashboard);
        } else {
          routerPush(PAGE_URLS.verificationDashboard);
        }
      }
    }
  }, [loginData]);
  useEffect(() => {
    if (loginError?.error?.dfgMessage) {
      addToast({
        content: loginError?.error?.dfgMessage ?? '',
        variant: 'error',
        autoCloseDuration: TOAST_ERROR_TIME
      });
    }
  }, [loginError]);
  const onClickEye = () => {
    setShowPassword(prev => !prev);
  };
  return <div className={styles.parentContainer} data-sentry-component="Login" data-sentry-source-file="index.tsx">
      <Loader show={loading || routerLoading} data-sentry-element="Loader" data-sentry-source-file="index.tsx" />
      <div className={styles.subHeaderLarge}>
        <SubHeader iconName={validIcons.checkMarkShadow} title={t('login.loginTitle')} description={t('login.loginDescription')} iconClassName={styles.iconSubheader} iconWidth="120px" baseUrl={process.env.NEXT_PUBLIC_STATIC_URL ?? ''} background="gridDotLg" data-sentry-element="SubHeader" data-sentry-source-file="index.tsx" />
      </div>
      <div className={styles.mainContainer}>
        <Input placeholder={t('login.emailPlaceholder')} onChange={handleChange} type="email" label={t('login.emailLabel')} value={values.email} name="email" errorMessage={submitClicked && errors.email} data-testid="inputBox" prefixIcon={<Icon iconName={validIcons.mail01} />} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
        <Input placeholder={t('login.passwordPlaceholder')} onChange={handleChange} label={t('login.passwordLabel')} name="password" type={showPassword ? 'text' : 'password'} data-testid="password-inputBox" suffixIcon={<Icon iconName={validIcons.eye} onClick={onClickEye} />} errorMessage={submitClicked && errors.password} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
        <div className={styles.forgotPassword}>
          <Button text={t('login.forgotButton')} variant="link" size={ButtonSize.Large} className={styles.forgotButton} onClick={handleForgotPassword} testPrefixId="forgot" data-sentry-element="Button" data-sentry-source-file="index.tsx" />
        </div>
        <div className={styles.buttonContainer}>
          <Button text={t('login.loginButton')} variant={ButtonType.Primary} size={ButtonSize.Large} className={styles.button} onClick={onSubmit} disabled={loading} testPrefixId="login" data-sentry-element="Button" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      {showBadge ? <Toast content={loginData?.message ?? ''} variant={loginData?.code === 200 ? 'success' : 'error'} onClose={() => {}} /> : null}
    </div>;
};
export default Login;